// src/components/pages/Admin/SubmissionsPage.js

import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
} from "@mui/material";
import { onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../../../firebase"; // Adjust path as needed
import SubmissionCard from "../Admin/SubmissionCard";

const SubmissionsPage = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  // Filter states
  const [userIdFilter, setUserIdFilter] = useState("");
  const [userEmailFilter, setUserEmailFilter] = useState("");
  const [challengeIdFilter, setChallengeIdFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // '' | 'published' | 'draft'

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (usr) => {
      if (usr) {
        const userDocRef = doc(db, "users", usr.uid);
        const docSnap = await getDoc(userDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setIsAdmin(!!data.admin);
        } else {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    async function fetchSubmissions() {
      try {
        const submissionsRef = collection(db, "submissions");
        const querySnapshot = await getDocs(submissionsRef);
        const submissionsData = [];
        querySnapshot.forEach((doc) => {
          submissionsData.push({ id: doc.id, ...doc.data() });
        });
        setSubmissions(submissionsData);
      } catch (error) {
        console.error("Error fetching submissions: ", error);
      }
    }
    fetchSubmissions();
  }, []);

  // Callback to update a submission’s payment status to COMPLETED in state
  const handleForcePaymentUpdate = (submissionId) => {
    setSubmissions((prevSubmissions) =>
      prevSubmissions.map((submission) =>
        submission.id === submissionId
          ? {
              ...submission,
              paymentData: {
                ...submission.paymentData,
                status: "COMPLETED",
              },
            }
          : submission
      )
    );
  };

  // Callback to remove a submission from state after deletion
  const handleDeleteSubmission = (submissionId) => {
    setSubmissions((prevSubmissions) =>
      prevSubmissions.filter((submission) => submission.id !== submissionId)
    );
  };

  // Filter submissions based on the filter inputs
  const filteredSubmissions = submissions.filter((submission) => {
    let match = true;

    if (userIdFilter) {
      match =
        match &&
        submission.userId &&
        submission.userId.toLowerCase().includes(userIdFilter.toLowerCase());
    }

    if (userEmailFilter) {
      const email =
        (submission.profileData && submission.profileData.email) || "";
      match =
        match && email.toLowerCase().includes(userEmailFilter.toLowerCase());
    }

    if (challengeIdFilter) {
      match =
        match &&
        submission.challengeId &&
        submission.challengeId
          .toLowerCase()
          .includes(challengeIdFilter.toLowerCase());
    }

    if (statusFilter) {
      match =
        match &&
        submission.status &&
        submission.status.toLowerCase() === statusFilter.toLowerCase();
    }

    return match;
  });

  // Show a loading indicator while checking auth status
  if (authLoading) {
    return (
      <Container sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container>
        <Typography variant="h5" color="error">
          Not authorized
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        mt: 4,
        pb: 1,
        fontFamily: "arial, sans-serif",
        backgroundColor: "#f6f6f6",
        borderRadius: "10px",
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          color: "#333",
          fontWeight: "normal",
          fontFamily: "arial, sans-serif",
          pt: 2,
          pb: 2,
        }}
      >
        Submission Admin
      </Typography>

      {/* Filter Controls */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="User ID"
              variant="outlined"
              fullWidth
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="User Email"
              variant="outlined"
              fullWidth
              value={userEmailFilter}
              onChange={(e) => setUserEmailFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Challenge ID"
              variant="outlined"
              fullWidth
              value={challengeIdFilter}
              onChange={(e) => setChallengeIdFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Render the filtered submission cards */}
      {filteredSubmissions.map((submission) => (
        <SubmissionCard
          key={submission.id}
          submission={submission}
          onForcePayment={handleForcePaymentUpdate}
          onDeleteSubmission={handleDeleteSubmission}
        />
      ))}
    </Container>
  );
};

export default SubmissionsPage;
